import type {
  CloudBoardState,
  CloudRestoreHistory,
  CloudSnapshotHistory,
} from "@board-preserve/contracts";

import {
  BrowserIcon,
  CloudIcon,
  PlanIcon,
  RecoveryIcon,
} from "./power-up-icons";
import { scheduleSummary } from "./plan-and-usage-panel";
import { TaskHubFooter } from "./task-hub-shell";

type CaptureOverviewState =
  | "accepted"
  | "cancelled"
  | "complete"
  | "failed"
  | "idle"
  | "paused"
  | "processing"
  | "submitting";

interface WorkspaceOverviewProps {
  captureMessage: string | null;
  capturePercent: number | null;
  captureState: CaptureOverviewState;
  cloudHistory: CloudSnapshotHistory | null;
  cloudRestoreHistory: CloudRestoreHistory | null;
  cloudState: CloudBoardState | null;
  historyPending?: boolean;
  onOpenBackup: () => void;
  onBackups: () => void;
  onLocalExport: () => void;
  onOpenActiveBackup: () => void;
  onOpenActiveRestore: () => void;
  onOpenRecoveryJob: (jobId: string) => void;
  onPlan: () => void;
  onRecovery: () => void;
  onSchedule: () => void;
  onSettings: () => void;
  onSetupCloud: () => void;
  restoreActive: boolean;
}

function formatShortDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function trialExpired(cloudState: CloudBoardState): boolean {
  return (
    cloudState.trial.status === "consumed" &&
    new Date(cloudState.trial.expiresAt).getTime() <= Date.now()
  );
}

export function ActiveWork({
  captureMessage,
  capturePercent,
  captureState,
  onOpenActiveBackup,
  onOpenActiveRestore,
  restoreActive,
}: Pick<
  WorkspaceOverviewProps,
  | "captureMessage"
  | "capturePercent"
  | "captureState"
  | "onOpenActiveBackup"
  | "onOpenActiveRestore"
  | "restoreActive"
>) {
  const captureActive = [
    "accepted",
    "processing",
    "paused",
    "submitting",
  ].includes(captureState);

  return (
    <>
      {captureActive ? (
        <button
          className="active-job-strip"
          onClick={onOpenActiveBackup}
          type="button"
        >
          <span className="active-job-spinner" aria-hidden="true" />
          <span>
            <strong>
              {captureState === "paused"
                ? "Backup paused"
                : "Backing up this board"}
            </strong>
            <small className="sr-only">
              {captureMessage ?? "Preparing the next recovery point."}
            </small>
            <span className="overview-job-track" aria-hidden="true">
              <span style={{ width: `${capturePercent ?? 0}%` }} />
            </span>
          </span>
          <b>{capturePercent === null ? "…" : `${capturePercent}%`}</b>
        </button>
      ) : null}
      {restoreActive ? (
        <button
          className="active-job-strip"
          onClick={onOpenActiveRestore}
          type="button"
        >
          <RecoveryIcon />
          <span>
            <strong>Recovery is in progress</strong>
            <small>
              Open the job to see progress and object-level results.
            </small>
          </span>
          <b>View</b>
        </button>
      ) : null}
    </>
  );
}

function LocalExportCard({ onLocalExport }: { onLocalExport: () => void }) {
  return (
    <section className="overview-choice-card">
      <div className="overview-choice-title">
        <BrowserIcon />
        <h2>Download a ZIP now</h2>
        <span className="feature-badge feature-badge-neutral">Always free</span>
      </div>
      <p>
        Built in this browser and saved to your downloads. No board content
        reaches Nodestone. Keep this Trello tab open while it runs.
      </p>
      <button
        className="secondary-action"
        onClick={onLocalExport}
        type="button"
      >
        Start local export
      </button>
    </section>
  );
}

function PaidPlanPrompt({ onPlan }: { onPlan: () => void }) {
  return (
    <section className="overview-paid-prompt">
      <h2>Keep this board protected</h2>
      <p>
        Paid plans add repeatable cloud backups, automatic schedules, longer
        history, comparison, and every managed recovery route.
      </p>
      <div>
        <button onClick={onPlan} type="button">
          View plans
        </button>
        <span>Plans start at £15 a month, including VAT.</span>
      </div>
    </section>
  );
}

function RecentJobs({
  cloudHistory,
  cloudRestoreHistory,
  onBackups,
  onOpenRecoveryJob,
}: Pick<
  WorkspaceOverviewProps,
  "cloudHistory" | "cloudRestoreHistory" | "onBackups" | "onOpenRecoveryJob"
>) {
  const jobs = [
    ...(cloudHistory?.snapshots ?? []).map((snapshot) => ({
      createdAt: snapshot.capturedAt,
      id: snapshot.id,
      kind: "backup" as const,
      snapshot,
    })),
    ...(cloudRestoreHistory?.restores ?? []).map((restore) => ({
      createdAt: restore.createdAt,
      id: restore.jobId,
      kind: "recovery" as const,
      restore,
    })),
  ]
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() -
        new Date(left.createdAt).getTime(),
    )
    .slice(0, 2);

  const jobCount =
    (cloudHistory?.snapshots.length ?? 0) +
    (cloudRestoreHistory?.restores.length ?? 0);
  if (jobs.length === 0) return null;

  return (
    <section className="overview-recent">
      <div className="overview-section-heading">
        <span>Recent jobs</span>
        <button onClick={onBackups} type="button">
          View all {jobCount}
        </button>
      </div>
      <div className="overview-recent-list">
        {jobs.map((job) => {
          if (job.kind === "recovery") {
            const active = ["pending", "running", "paused"].includes(
              job.restore.status,
            );
            return (
              <button
                key={`recovery:${job.id}`}
                onClick={() => onOpenRecoveryJob(job.id)}
                type="button"
              >
                <span
                  className={`status-dot${job.restore.status === "failed" ? " failed" : job.restore.status === "complete_with_warnings" ? " warning" : ""}`}
                />
                <span>
                  <strong>{formatShortDate(job.createdAt)}</strong>
                  <small>
                    {job.restore.scope === "all"
                      ? "Recovery board"
                      : job.restore.scope === "card"
                        ? "Card recovery"
                        : "List recovery"}{" "}
                    · {job.restore.progress.completed} of{" "}
                    {job.restore.progress.total} operations
                  </small>
                </span>
                <b>
                  {active
                    ? "In progress"
                    : job.restore.status === "complete_with_warnings"
                      ? "Warnings"
                      : job.restore.status === "failed"
                        ? "Failed"
                        : job.restore.status === "canceled"
                          ? "Cancelled"
                          : "Complete"}
                </b>
              </button>
            );
          }
          const snapshot = job.snapshot;
          return (
            <button
              key={`backup:${snapshot.id}`}
              onClick={onBackups}
              type="button"
            >
              <span
                className={`status-dot${snapshot.warningCount > 0 ? " warning" : ""}`}
              />
              <span>
                <strong>{formatShortDate(snapshot.capturedAt)}</strong>
                <small>
                  {snapshot.capturePurpose === "restore_safety"
                    ? "Safety backup"
                    : "Cloud backup"}{" "}
                  · {snapshot.attachmentOutcomes.copied} files copied · expires{" "}
                  {formatShortDate(snapshot.expiresAt)}
                </small>
              </span>
              <b>
                {snapshot.warningCount > 0
                  ? `${snapshot.warningCount} warning${snapshot.warningCount === 1 ? "" : "s"}`
                  : "Complete"}
              </b>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function TrialOverview({
  captureState,
  cloudHistory,
  cloudRestoreHistory,
  cloudState,
  historyPending,
  onOpenBackup,
  onBackups,
  onLocalExport,
  onPlan,
  onRecovery,
  onOpenRecoveryJob,
}: Pick<
  WorkspaceOverviewProps,
  | "cloudHistory"
  | "cloudRestoreHistory"
  | "cloudState"
  | "captureState"
  | "historyPending"
  | "onOpenBackup"
  | "onBackups"
  | "onLocalExport"
  | "onPlan"
  | "onRecovery"
  | "onOpenRecoveryJob"
>) {
  if (!cloudState || cloudState.trial.status === "available") {
    return (
      <div className="overview-stack">
        <div className="overview-intro">
          <h1>This board is not backed up yet</h1>
          <p>Choose where the copy should live. You can use both.</p>
        </div>
        <LocalExportCard onLocalExport={onLocalExport} />
        <section className="overview-choice-card overview-choice-cloud">
          <div className="overview-choice-title">
            <CloudIcon />
            <h2>Protect this board in the cloud</h2>
            <span className="feature-badge">Trial</span>
          </div>
          <p>
            One free managed backup for this Workspace, kept for 48 hours in
            private EU storage.
          </p>
          <ul className="overview-feature-list">
            <li>
              Attachment files are copied when Trello makes them available.
            </li>
            <li>One recovery board can be created from an uploaded ZIP.</li>
            <li className="not-included">
              No schedules, comparison, or recovery onto this board.
            </li>
          </ul>
          <button onClick={onOpenBackup} type="button">
            Use my free backup
          </button>
        </section>
        <p className="overview-inline-route">
          Already have a Board Preserve ZIP?{" "}
          <button onClick={onRecovery} type="button">
            Restore from a ZIP
          </button>
        </p>
        <PaidPlanPrompt onPlan={onPlan} />
      </div>
    );
  }

  if (trialExpired(cloudState)) {
    return (
      <div className="overview-stack">
        <section className="overview-status-card overview-status-expired">
          <RecoveryIcon />
          <div>
            <h1>Your trial backup has expired</h1>
            <p>
              It was removed after the 48-hour trial period. Any ZIP you
              downloaded is still yours.
            </p>
          </div>
        </section>
        <PaidPlanPrompt onPlan={onPlan} />
        <LocalExportCard onLocalExport={onLocalExport} />
      </div>
    );
  }

  const latest = cloudHistory?.snapshots[0] ?? null;
  const captureActive = [
    "accepted",
    "paused",
    "processing",
    "submitting",
  ].includes(captureState);
  const backupUnavailable = !latest && !captureActive && !historyPending;
  return (
    <div className="overview-stack">
      <section
        className={`overview-status-card${latest ? " overview-status-ready" : ""}`}
      >
        <CloudIcon />
        <div>
          <h1>
            {latest
              ? "Your trial backup is ready"
              : captureActive
                ? "Your trial backup is in progress"
                : historyPending
                  ? "Checking your trial backup"
                  : "Your trial backup is no longer available"}
          </h1>
          <p>
            {latest
              ? `Taken ${formatShortDate(latest.capturedAt)}. Download it before ${formatShortDate(latest.expiresAt)} and keep it for good.`
              : captureActive
                ? "Board Preserve is preparing this Workspace’s one-time backup. You can close this window; it will keep working."
                : historyPending
                  ? "Board Preserve is checking this Workspace’s one-time backup."
                  : "It was deleted or reached its 48-hour expiry. Any ZIP you downloaded is still yours."}
          </p>
        </div>
        {!latest && !backupUnavailable ? (
          <button onClick={onOpenBackup} type="button">
            View progress
          </button>
        ) : null}
      </section>
      <RecentJobs
        cloudHistory={cloudHistory}
        cloudRestoreHistory={cloudRestoreHistory}
        onBackups={onBackups}
        onOpenRecoveryJob={onOpenRecoveryJob}
      />
      <PaidPlanPrompt onPlan={onPlan} />
      <div className="overview-free-actions">
        <span>Still free whenever you need them:</span>
        <button
          className="secondary-action"
          onClick={onLocalExport}
          type="button"
        >
          Download a local ZIP
        </button>
        <button className="secondary-action" onClick={onRecovery} type="button">
          Restore from a ZIP
        </button>
      </div>
    </div>
  );
}

function ProOverview({
  cloudHistory,
  cloudRestoreHistory,
  cloudState,
  historyPending,
  onOpenBackup,
  onBackups,
  onLocalExport,
  onPlan,
  onRecovery,
  onOpenRecoveryJob,
  onSchedule,
}: Pick<
  WorkspaceOverviewProps,
  | "cloudHistory"
  | "cloudRestoreHistory"
  | "cloudState"
  | "historyPending"
  | "onOpenBackup"
  | "onBackups"
  | "onLocalExport"
  | "onPlan"
  | "onRecovery"
  | "onOpenRecoveryJob"
  | "onSchedule"
>) {
  if (!cloudState) return null;
  const latest = cloudHistory?.snapshots[0] ?? null;
  const schedule = scheduleSummary(cloudState.settings);

  return (
    <div className="overview-stack">
      <section className="overview-status-card overview-status-protected">
        <CloudIcon />
        <div>
          <h1>This board is protected</h1>
          <p>
            {latest
              ? `Last backup ${formatShortDate(latest.capturedAt)}. `
              : historyPending
                ? "Checking the latest backup. "
                : "No completed backup yet. "}
            {schedule === "Manual only"
              ? "Automatic backups are off."
              : `${schedule} backups are on${cloudState.settings.nextSnapshotAt ? `; next ${formatShortDate(cloudState.settings.nextSnapshotAt)}` : ""}.`}
          </p>
        </div>
        <div className="overview-status-actions">
          <button onClick={onOpenBackup} type="button">
            Backup now
          </button>
          <button
            className="secondary-action"
            onClick={onSchedule}
            type="button"
          >
            Edit schedule
          </button>
        </div>
        <p className="custody-note">
          Private EU storage, encrypted in transit and at rest.
        </p>
      </section>
      <RecentJobs
        cloudHistory={cloudHistory}
        cloudRestoreHistory={cloudRestoreHistory}
        onBackups={onBackups}
        onOpenRecoveryJob={onOpenRecoveryJob}
      />
      <section className="overview-other-tasks">
        <div className="overview-section-heading">
          <span>Other tasks</span>
        </div>
        <div>
          <button onClick={onLocalExport} type="button">
            <BrowserIcon />
            <span>
              <strong>Download a ZIP now</strong>
              <small>Built in this browser. Nothing is sent to us.</small>
            </span>
          </button>
          <button onClick={onRecovery} type="button">
            <RecoveryIcon />
            <span>
              <strong>Recover something</strong>
              <small>From a cloud backup or uploaded ZIP.</small>
            </span>
          </button>
          <button onClick={onPlan} type="button">
            <PlanIcon />
            <span>
              <strong>Plan and usage</strong>
              <small>Review this Workspace’s allowances.</small>
            </span>
          </button>
        </div>
      </section>
    </div>
  );
}

export function WorkspaceOverview(props: WorkspaceOverviewProps) {
  const { cloudState } = props;
  const paid =
    cloudState !== null && cloudState.entitlements.effectivePlan !== "free";

  return (
    <section
      className="workspace-overview"
      aria-label="Board protection overview"
    >
      <ActiveWork {...props} />
      {!cloudState ? (
        <div className="overview-stack">
          <div className="overview-intro">
            <h1 id="overview-title">This board is not backed up yet</h1>
            <p>Choose where the copy should live. You can use both.</p>
          </div>
          <LocalExportCard onLocalExport={props.onLocalExport} />
          <section className="overview-choice-card overview-choice-cloud">
            <div className="overview-choice-title">
              <CloudIcon />
              <h2>Protect this board in the cloud</h2>
              <span className="feature-badge">Trial</span>
            </div>
            <p>
              Keep a managed recovery point after this Trello tab closes, with
              eligible attachment files copied to private EU storage.
            </p>
            <button onClick={props.onSetupCloud} type="button">
              Set up Cloud Preserve
            </button>
          </section>
          <p className="overview-inline-route">
            Already have a Board Preserve ZIP?{" "}
            <button onClick={props.onRecovery} type="button">
              Restore from a ZIP
            </button>
          </p>
          <PaidPlanPrompt onPlan={props.onPlan} />
        </div>
      ) : paid ? (
        <ProOverview {...props} cloudState={cloudState} />
      ) : (
        <TrialOverview {...props} cloudState={cloudState} />
      )}
      <TaskHubFooter onPlan={props.onPlan} onSettings={props.onSettings} />
    </section>
  );
}
